import { useEffect, useState } from 'react';
import { Button, Card } from 'antd';
import {
  ClipboardDocumentListIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import TextWinds from '../../../components/common/TextWinds';
import RecitationClassPanel from '../../../components/lms/recitation/status/RecitationClassPanel';
import { useSearchParams } from 'react-router-dom';
import { openNewWindow } from '../../../api/utils/util-func';
import RecitationStudentPanel from '../../../components/lms/recitation/status/RecitationStudentPanel';
import TopButton from '../../../components/common/TopButton';

type RecitationStatusViewType = 'CLASS' | 'STUDENT';

const recitationStatusViewOptions: {
  value: RecitationStatusViewType;
  title: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'CLASS',
    title: '반별로 보기',
    description: '반 단위로 AI인증제 진행 현황을 확인합니다.',
    icon: <UserGroupIcon className="h-5 w-5 xl:h-7 xl:w-7" />,
  },
  {
    value: 'STUDENT',
    title: '학생별로 보기',
    description: '학생별 제출 여부와 AI인증제 상태를 확인합니다.',
    icon: <ClipboardDocumentListIcon className="h-5 w-5 xl:h-7 xl:w-7" />,
  },
];

/**
 * 개편 LMS (교사) - AI인증제 관리 > AI인증제 현황 페이지 > 상단 & 탭 & 콘텐츠
 */
const RecitationStatus = () => {
  const [_, setSearchParams] = useSearchParams();

  const [viewType, setViewType] = useState<RecitationStatusViewType>('CLASS');

  useEffect(() => {
    setSearchParams({});
  }, [viewType]);

  return (
    <div className="flex w-full flex-col pt-6 xl:px-5">
      <header className="flex flex-col gap-3 px-5 xl:flex-row xl:items-center xl:justify-between xl:px-0">
        <div className="min-w-0">
          <TextWinds type="title_h2" xlType="title_h3" className="mb-[2px]">
            AI인증제 현황보기
          </TextWinds>

          <TextWinds type="content_body3">
            학생에게 내준 AI인증제의 진행 상태를 확인할 수 있습니다. 반별 또는
            학생별로 AI인증제 제출 현황을 관리해 주세요.
          </TextWinds>
        </div>

        <Button
          type="primary"
          className="shrink-0 rounded-lg !border-purple5 !bg-purple5 px-4 hover:!border-purple6 hover:!bg-purple6"
          onClick={() => {
            const url = 'https://cafe.naver.com/jemm/5132';
            openNewWindow(url);
          }}
        >
          이용 안내
        </Button>
      </header>

      <div className="my-5 flex flex-col gap-4 px-3 xl:px-0">
        <div className="grid grid-cols-2 gap-3">
          {recitationStatusViewOptions.map((option) => {
            const isSelected = viewType === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setViewType(option.value)}
                className="w-full rounded-xl text-left transition-all duration-200 active:scale-[0.99]"
              >
                <Card
                  className={[
                    'h-full rounded-xl border transition-all duration-200',
                    isSelected
                      ? 'border-purple5 bg-purple1 shadow-sm'
                      : 'border-gray3 bg-white hover:border-purple3 hover:bg-gray1 hover:shadow-sm',
                  ].join(' ')}
                  styles={{
                    body: {
                      padding: 10,
                    },
                  }}
                >
                  <div className="flex items-center gap-2 xl:gap-3">
                    <div
                      className={[
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full xl:h-10 xl:w-10',
                        isSelected
                          ? 'bg-purple5 text-white'
                          : 'bg-gray1 text-gray8',
                      ].join(' ')}
                    >
                      {option.icon}
                    </div>

                    <div className="min-w-0">
                      <TextWinds
                        type="content_body2"
                        className={[
                          'block truncate break-keep font-bold',
                          isSelected ? 'text-purple5' : '',
                        ].join(' ')}
                      >
                        {option.title}
                      </TextWinds>

                      <TextWinds
                        type="caption1"
                        color="gray8"
                        className="mt-0.5 hidden break-keep xl:block"
                      >
                        {option.description}
                      </TextWinds>
                    </div>
                  </div>
                </Card>
              </button>
            );
          })}
        </div>

        <section className="rounded-xl bg-white p-4 shadow-card xl:p-6">
          {viewType === 'CLASS' ? (
            <RecitationClassPanel />
          ) : (
            <RecitationStudentPanel />
          )}
        </section>
      </div>

      <TopButton />
    </div>
  );
};

export default RecitationStatus;
